import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, Edit2, Trash2, X, Calendar, Shuffle } from "lucide-react";
import { WeeklyDistributionModal } from "@/components/goals/WeeklyDistributionModal";

function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AdminGoals() {
  const [month, setMonth] = useState(ymNow());
  const [weekIndex, setWeekIndex] = useState("");
  const [techId, setTechId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [techs, setTechs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [progress, setProgress] = useState(null);
  const [goals, setGoals] = useState([]);
  const [modal, setModal] = useState(null); // {technician_id, scope, month, week_index?, orders_target, sales_target}
  const [loading, setLoading] = useState(false);
  const [dist, setDist] = useState(null); // {data, monthly}
  const [distBusy, setDistBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "technician" && u.active))).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data.filter((b) => b.active))).catch(() => {});
  }, []);

  async function reload() {
    setLoading(true);
    try {
      const p = new URLSearchParams({ month });
      if (weekIndex) p.set("week_index", weekIndex);
      if (techId) p.set("technician_id", techId);
      if (branchId) p.set("branch_id", branchId);
      const [pr, gs] = await Promise.all([
        api.get(`/admin/production-goals/progress?${p.toString()}`),
        api.get(`/admin/production-goals?month=${month}${techId ? `&technician_id=${techId}` : ""}`),
      ]);
      setProgress(pr.data);
      setGoals(gs.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [month, weekIndex, techId, branchId]);

  async function saveGoal() {
    const payload = {
      technician_id: modal.technician_id,
      scope: modal.scope,
      month: modal.month,
      orders_target: Number(modal.orders_target || 0),
      sales_target: Number(modal.sales_target || 0),
      active: true,
    };
    if (modal.scope === "monthly") return openDistribution(payload);
    if (modal.scope === "weekly") {
      const wi = Number(modal.week_index);
      payload.week_index = Number.isInteger(wi) && wi >= 1 && wi <= 6 ? wi : 1;
    }
    try {
      await api.post("/admin/production-goals", payload);
      toast.success("Meta guardada");
      setModal(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function openDistribution(monthly) {
    try {
      const p = new URLSearchParams({ month: monthly.month, technician_id: monthly.technician_id, orders_target: monthly.orders_target, sales_target: monthly.sales_target });
      const r = await api.get(`/admin/production-goals/weekly-distribution?${p.toString()}`);
      setDist({ data: r.data, monthly });
      setModal(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function confirmDistribution(weeks) {
    setDistBusy(true);
    try {
      await api.post("/admin/production-goals", dist.monthly);
      if (weeks) {
        await api.post("/admin/production-goals/weekly-bulk", { technician_id: dist.monthly.technician_id, month: dist.monthly.month, weeks });
      }
      toast.success(weeks ? "Meta mensual y semanas guardadas" : "Meta mensual guardada");
      setDist(null);
      setModal(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDistBusy(false); }
  }

  async function deleteGoal(g) {
    if (!window.confirm(`¿Eliminar meta ${g.scope} de ${g.month}${g.week_index ? ` semana ${g.week_index}` : ""}?`)) return;
    try {
      await api.delete(`/admin/production-goals/${g.id}`);
      toast.success("Meta eliminada");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const goalsByTech = useMemo(() => {
    const m = {};
    for (const g of goals) {
      m[g.technician_id] = m[g.technician_id] || { monthly: null, weekly: {} };
      if (g.scope === "monthly") m[g.technician_id].monthly = g;
      else m[g.technician_id].weekly[g.week_index] = g;
    }
    return m;
  }, [goals]);

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-heading font-extrabold text-3xl text-slate-900 flex items-center gap-2">
          <Target size={28} className="text-blue-600" /> Metas de producción
        </h1>
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Solo administrador</span>
      </header>

      <div className="card-base p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Mes</label>
          <input type="month" className="input-base" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="goals-filter-month" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Semana</label>
          <select className="input-base" value={weekIndex} onChange={(e) => setWeekIndex(e.target.value)} data-testid="goals-filter-week">
            <option value="">Todas</option>
            {[1, 2, 3, 4, 5, 6].map((w) => <option key={w} value={w}>Semana {w}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Técnico</label>
          <select className="input-base" value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="goals-filter-tech">
            <option value="">Todos</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Sucursal</label>
          <select className="input-base" value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="goals-filter-branch">
            <option value="">Todas</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button className="btn-primary w-full min-h-[42px]" onClick={() => setModal({ scope: "monthly", month, technician_id: techId || (techs[0]?.id || ""), orders_target: "", sales_target: "" })} data-testid="goals-create-btn">
            <Plus size={16} /> Nueva meta
          </button>
        </div>
      </div>

      {loading && <div className="text-center text-slate-500 py-6">Cargando…</div>}

      {!loading && progress && (
        <div className="card-base overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-heading font-bold text-slate-900">Progreso por técnico — {month}{weekIndex ? ` · Semana ${weekIndex}` : ""}</h3>
          </div>
          {progress.rows.length === 0 ? (
            <div className="text-center text-slate-500 py-8">Sin técnicos que mostrar.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Técnico</th>
                    <th className="text-right px-3 py-2">Órdenes (mes)</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Producción (mes)</th>
                    <th className="text-right px-3 py-2">Meta $ (mes)</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">% Cumplimiento</th>
                    <th className="text-right px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {progress.rows.map((r) => {
                    const tg = goalsByTech[r.technician_id] || { monthly: null, weekly: {} };
                    return (
                      <tr key={r.technician_id} className="border-t border-slate-100" data-testid={`goals-progress-row-${r.technician_id}`}>
                        <td className="px-3 py-2 font-medium">{r.technician_name}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          <b>{r.monthly_orders_done}</b>{r.monthly_orders_target != null && <span className="text-slate-400"> / {r.monthly_orders_target}</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono hidden sm:table-cell">${r.monthly_sales_done.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600">
                          {r.monthly_sales_target != null ? `$${r.monthly_sales_target.toFixed(2)}` : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right hidden sm:table-cell">
                          {r.monthly_orders_pct != null || r.monthly_sales_pct != null ? (
                            <span className={`font-mono font-semibold ${(Math.max(r.monthly_orders_pct || 0, r.monthly_sales_pct || 0)) >= 100 ? "text-emerald-700" : "text-blue-700"}`}>
                              {Math.round(Math.max(r.monthly_orders_pct || 0, r.monthly_sales_pct || 0))}%
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            className="btn-secondary py-1 px-2 text-xs"
                            onClick={() => setModal({
                              id: tg.monthly?.id,
                              scope: "monthly",
                              month,
                              technician_id: r.technician_id,
                              orders_target: tg.monthly?.orders_target ?? "",
                              sales_target: tg.monthly?.sales_target ?? "",
                            })}
                            data-testid={`goals-edit-monthly-${r.technician_id}`}
                          >
                            <Edit2 size={12} /> Meta mes
                          </button>
                          <button
                            className="btn-secondary py-1 px-2 text-xs ml-1"
                            onClick={() => setModal({
                              scope: "weekly",
                              month,
                              technician_id: r.technician_id,
                              week_index: weekIndex || 1,
                              orders_target: (tg.weekly[weekIndex || 1]?.orders_target) ?? "",
                              sales_target: (tg.weekly[weekIndex || 1]?.sales_target) ?? "",
                            })}
                            data-testid={`goals-edit-weekly-${r.technician_id}`}
                          >
                            Sem
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Rendimiento semanal por técnico — tarjetas visuales con fechas, metas y
         progreso. Reutiliza `/admin/production-goals/progress` que ya devuelve
         `rows[].weeks[]` con fechas y datos reales (production_snapshot). */}
      {!loading && progress && progress.rows.length > 0 && (
        <div className="space-y-4" data-testid="weekly-performance">
          {progress.rows.map((r) => (
            <div key={`wk-${r.technician_id}`} className="card-base overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                  <Calendar size={16} className="text-emerald-600" />
                  Rendimiento semanal — {r.technician_name}
                </h3>
                <div className="text-xs font-mono text-slate-500 flex items-center gap-2 flex-wrap">
                  <span>
                    Mes: <b>{r.monthly_orders_done}</b>/{r.monthly_orders_target ?? "—"} órdenes ·
                    <b className="ml-1">${r.monthly_sales_done.toFixed(2)}</b>/{r.monthly_sales_target != null ? `$${r.monthly_sales_target.toFixed(2)}` : "—"}
                  </span>
                  <WeekSumBadge row={r} />
                  {r.monthly_orders_target != null && (
                    <button
                      className="btn-secondary py-1 px-2 text-xs"
                      onClick={() => openDistribution({ technician_id: r.technician_id, scope: "monthly", month, orders_target: r.monthly_orders_target, sales_target: r.monthly_sales_target, active: true })}
                      data-testid={`goals-redistribute-${r.technician_id}`}
                    >
                      <Shuffle size={12} /> Recalcular semanas
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 p-3">
                {r.weeks.map((w) => {
                  const hasGoal = w.orders_target != null || w.sales_target != null;
                  const op = w.orders_pct ?? 0;
                  const sp = w.sales_pct ?? 0;
                  const stateLabel = !hasGoal ? "Sin meta" : (op >= 100 && sp >= 100) ? "Cumplida" : _weekPast(w.end) ? "No cumplida" : "En progreso";
                  const stateClass = !hasGoal ? "bg-slate-100 text-slate-500" :
                    stateLabel === "Cumplida" ? "bg-emerald-100 text-emerald-800" :
                    stateLabel === "No cumplida" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800";
                  const diffOrd = w.orders_target != null ? w.orders_done - w.orders_target : null;
                  const diffSale = w.sales_target != null ? w.sales_done - w.sales_target : null;
                  return (
                    <div
                      key={`${r.technician_id}-${w.week_index}`}
                      className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2"
                      data-testid={`week-card-${r.technician_id}-${w.week_index}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-heading font-bold text-slate-900">S{w.week_index}</div>
                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${stateClass}`}>{stateLabel}</span>
                      </div>
                      <div className="text-xs font-mono text-slate-500">
                        {_fmtDMY(w.start)} – {_fmtDMY(w.end)}
                      </div>

                      {/* Órdenes */}
                      <div>
                        <div className="flex items-baseline justify-between text-xs">
                          <span className="text-slate-500 uppercase font-mono tracking-wider">Órdenes</span>
                          <span className="font-mono">
                            <b className="text-slate-900">{w.orders_done}</b>
                            <span className="text-slate-400"> / {w.orders_target ?? "—"}</span>
                          </span>
                        </div>
                        <_Bar pct={w.orders_pct} tone="emerald" />
                        <div className="flex justify-between text-[11px] font-mono mt-1">
                          <span className={w.orders_pct != null && w.orders_pct >= 100 ? "text-emerald-700 font-semibold" : "text-slate-500"}>
                            {w.orders_pct == null ? "—" : `${Math.round(w.orders_pct)}%`}
                          </span>
                          {diffOrd != null && (
                            <span className={diffOrd >= 0 ? "text-emerald-600" : "text-rose-600"}>
                              {diffOrd >= 0 ? "+" : ""}{diffOrd}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Producción */}
                      <div>
                        <div className="flex items-baseline justify-between text-xs">
                          <span className="text-slate-500 uppercase font-mono tracking-wider">Producción</span>
                          <span className="font-mono">
                            <b className="text-slate-900">${w.sales_done.toFixed(2)}</b>
                            <span className="text-slate-400"> / {w.sales_target != null ? `$${w.sales_target.toFixed(2)}` : "—"}</span>
                          </span>
                        </div>
                        <_Bar pct={w.sales_pct} tone="blue" />
                        <div className="flex justify-between text-[11px] font-mono mt-1">
                          <span className={w.sales_pct != null && w.sales_pct >= 100 ? "text-blue-700 font-semibold" : "text-slate-500"}>
                            {w.sales_pct == null ? "—" : `${Math.round(w.sales_pct)}%`}
                          </span>
                          {diffSale != null && (
                            <span className={diffSale >= 0 ? "text-emerald-600" : "text-rose-600"}>
                              {diffSale >= 0 ? "+" : ""}${diffSale.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        className="btn-secondary text-xs py-1 mt-auto"
                        onClick={() => setModal({
                          id: w.goal_id,
                          scope: "weekly",
                          month,
                          technician_id: r.technician_id,
                          week_index: w.week_index,
                          orders_target: w.orders_target ?? "",
                          sales_target: w.sales_target ?? "",
                        })}
                        data-testid={`week-edit-${r.technician_id}-${w.week_index}`}
                      >
                        <Edit2 size={11} /> {hasGoal ? "Editar meta" : "Definir meta"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Existing goals table for admin management */}
      {goals.length > 0 && (
        <div className="card-base overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-heading font-bold text-slate-900">Metas configuradas</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Técnico</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-right px-3 py-2">Órdenes</th>
                  <th className="text-right px-3 py-2">$ Meta</th>
                  <th className="text-right px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {goals.map((g) => {
                  const tech = techs.find((t) => t.id === g.technician_id);
                  return (
                    <tr key={g.id} className="border-t border-slate-100" data-testid={`goals-row-${g.id}`}>
                      <td className="px-3 py-2">{tech?.name || g.technician_id.slice(0, 8)}</td>
                      <td className="px-3 py-2">
                        {g.scope === "monthly" ? <span className="chip bg-blue-50 text-blue-700 border border-blue-200">Mensual</span> : (
                          <span className="chip bg-amber-50 text-amber-700 border border-amber-200">Semana {g.week_index}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{g.orders_target}</td>
                      <td className="px-3 py-2 text-right font-mono">${g.sales_target.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setModal({ ...g, technician_id: g.technician_id })} className="p-1.5 rounded hover:bg-blue-100 text-blue-700" data-testid={`goals-edit-${g.id}`}>
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => deleteGoal(g)} className="p-1.5 rounded hover:bg-red-100 text-red-600" data-testid={`goals-delete-${g.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {dist && (
        <WeeklyDistributionModal data={dist.data} monthly={dist.monthly} busy={distBusy} onClose={() => setDist(null)} onConfirm={confirmDistribution} />
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="goals-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">{modal.id ? "Editar meta" : "Nueva meta"}</h3>
              <button onClick={() => setModal(null)} className="p-1 rounded hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Técnico</label>
              <select className="input-base" value={modal.technician_id} onChange={(e) => setModal({ ...modal, technician_id: e.target.value })} disabled={!!modal.id} data-testid="goals-modal-tech">
                {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Tipo</label>
              <select className="input-base" value={modal.scope} onChange={(e) => {
                const scope = e.target.value;
                // Al pasar a semanal, inicializar week_index=1 si no está seteado,
                // para evitar enviar null/NaN cuando el usuario no toca el combo.
                setModal({
                  ...modal,
                  scope,
                  week_index: scope === "weekly" ? (modal.week_index || 1) : modal.week_index,
                });
              }} disabled={!!modal.id} data-testid="goals-modal-scope">
                <option value="monthly">Mensual</option>
                <option value="weekly">Semanal</option>
              </select>
            </div>
            {modal.scope === "weekly" && (
              <div>
                <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Semana (1-6)</label>
                <select className="input-base" value={modal.week_index} onChange={(e) => setModal({ ...modal, week_index: e.target.value })} disabled={!!modal.id} data-testid="goals-modal-week">
                  {[1, 2, 3, 4, 5, 6].map((w) => <option key={w} value={w}>Semana {w}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Meta órdenes</label>
                <input type="number" min="0" className="input-base font-mono" value={modal.orders_target} onChange={(e) => setModal({ ...modal, orders_target: e.target.value })} data-testid="goals-modal-orders" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Meta $ producción</label>
                <input type="number" min="0" step="0.01" className="input-base font-mono" value={modal.sales_target} onChange={(e) => setModal({ ...modal, sales_target: e.target.value })} data-testid="goals-modal-sales" />
              </div>
            </div>
            <button className="btn-primary w-full py-3" onClick={saveGoal} data-testid="goals-modal-save">Guardar meta</button>
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Helpers UI para tarjetas semanales
// -----------------------------------------------------------------------------
function _fmtDMY(iso) {
  if (!iso) return "—";
  // iso es "YYYY-MM-DD" (date simple del backend), evitamos new Date para no
  // introducir timezone drift. "2026-10-01" → "01/10".
  const [, mo, dd] = iso.split("-");
  return `${dd}/${mo}`;
}

function _weekPast(endIso) {
  if (!endIso) return false;
  // "YYYY-MM-DD" inclusivo (último día de la semana)
  const now = new Date();
  const [y, m, d] = endIso.split("-").map(Number);
  // La semana se considera pasada cuando "hoy" ya está más allá del día siguiente al end
  const endPlus1 = new Date(y, (m || 1) - 1, (d || 1) + 1);
  return now >= endPlus1;
}

function WeekSumBadge({ row }) {
  const set = row.weeks.filter((w) => w.orders_target != null);
  if (!set.length || row.monthly_orders_target == null) return null;
  const so = set.reduce((a, w) => a + (w.orders_target || 0), 0);
  const ss = Math.round(set.reduce((a, w) => a + (w.sales_target || 0), 0));
  const ok = so === row.monthly_orders_target && ss === Math.round(row.monthly_sales_target || 0);
  return (
    <span className={`px-2 py-0.5 rounded ${ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={`goals-weeksum-${row.technician_id}`}>
      Semanas: {so} órd · ${ss} {ok ? "= meta" : "≠ meta mensual"}
    </span>
  );
}

function _Bar({ pct, tone = "emerald" }) {
  const p = Math.max(0, Math.min(100, pct == null ? 0 : pct));
  const color = tone === "blue" ? "bg-blue-500" : tone === "amber" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all`} style={{ width: `${p}%` }} />
    </div>
  );
}
